import type { IPOI } from '@/shared/api/types/poi-types'
import type { ITourPoi } from '@/shared/api/types/tour-types'
import { DEFAULT_CSS_COLOR_NAME } from '@/shared/constants/css-color-names'

/**
 * POIs by id: the ones fetched for the picker plus the ones already attached to the tour, so a selected POI
 * stays resolvable (name, coordinates) even when it is no longer in the picker's current page/filter.
 */
export function buildPoiLookup(pois: IPOI[], selectedPois: ITourPoi[]): Record<string, IPOI> {
  const lookup: Record<string, IPOI> = Object.fromEntries(pois.map((poi) => [poi.id, poi]))
  selectedPois.forEach((item) => {
    if (item.poi && !lookup[item.poiId]) {
      lookup[item.poiId] = item.poi
    }
  })
  return lookup
}

/**
 * VI: POI "ngoài phạm vi" là POI không thuộc điểm đến (tenant destination) đang chọn cho tour; UI đánh dấu để admin gỡ chúng.
 * EN: An "out of scope" POI does not belong to the tour's selected tenant destination; the UI flags it so the admin can remove it.
 */
export function findOutOfScopePoiIds(
  selectedPois: ITourPoi[],
  poiLookup: Record<string, IPOI>,
  tenantDestinationId: string,
): Set<string> {
  const outOfScope = new Set<string>()
  selectedPois.forEach((item) => {
    const destinationId = (poiLookup[item.poiId] ?? item.poi)?.tenantDestinationId
    if (!destinationId || destinationId !== tenantDestinationId) {
      outOfScope.add(item.poiId)
    }
  })
  return outOfScope
}

/** Appends POIs not selected yet, numbering them after the existing ones; already-selected POIs are skipped (returns the input array if none is new). */
export function appendPois(selectedPois: ITourPoi[], poisToAdd: IPOI[]): ITourPoi[] {
  const selectedIds = new Set(selectedPois.map((item) => item.poiId))
  const next = [...selectedPois]
  poisToAdd.forEach((poi) => {
    if (!selectedIds.has(poi.id)) {
      next.push({ poiId: poi.id, poi, order: next.length + 1 })
      selectedIds.add(poi.id)
    }
  })
  // Same array when nothing was added, so React can skip the re-render.
  return next.length === selectedPois.length ? selectedPois : next
}

export interface TourPoiChanges {
  toAdd: ITourPoi[]
  toRemove: ITourPoi[]
  /** POIs whose colour or favourite rank must be PATCHed. */
  toUpdate: ITourPoi[]
}

/**
 * VI: Tính phần chênh lệch giữa POI trên server và POI đang chọn để chỉ gọi API cho những gì thực sự đổi (thêm, xoá, đổi màu/hạng yêu thích). Thứ tự được lưu riêng sau cùng.
 * EN: Diffs the server POIs against the current selection so only real changes hit the API (add, remove, colour/favourite rank). Order is saved separately afterwards.
 */
export function diffTourPois(serverPois: ITourPoi[], currentPois: ITourPoi[]): TourPoiChanges {
  const serverById = new Map(serverPois.map((poi) => [poi.poiId, poi]))
  const currentIds = new Set(currentPois.map((poi) => poi.poiId))

  const toAdd = currentPois.filter((poi) => !serverById.has(poi.poiId))
  const toRemove = serverPois.filter((poi) => !currentIds.has(poi.poiId))
  const toUpdate = currentPois.filter((poi) => {
    const server = serverById.get(poi.poiId)
    if (!server) {
      // A new POI only needs a PATCH when it carries a non-default colour or a favourite rank.
      return (
        (poi.color !== null && poi.color !== undefined && poi.color !== DEFAULT_CSS_COLOR_NAME) ||
        (poi.favoritePoiRank !== null && poi.favoritePoiRank !== undefined)
      )
    }
    return (
      (poi.color ?? null) !== (server.color ?? null) ||
      (poi.favoritePoiRank ?? null) !== (server.favoritePoiRank ?? null)
    )
  })

  return { toAdd, toRemove, toUpdate }
}
